import { FC, ReactNode, useEffect } from 'react';
import { Box } from '@mui/material';
import useNoteFormatDisplayStore from '@/stores/noteFormatDisplayStore';
import { InlineFormatResult } from '@/features/noteFormats';
import { StyledTabs, StyledTab } from './styles';

interface NoteContentTabsProps {
  children: ReactNode;
  readOnly?: boolean;
}

const NoteContentTabs: FC<NoteContentTabsProps> = ({ children, readOnly = false }) => {
  const generatedFormats = useNoteFormatDisplayStore((s) => s.generatedFormats);
  const displayNameMap = useNoteFormatDisplayStore((s) => s.displayNameMap);
  const activeTab = useNoteFormatDisplayStore((s) => s.activeTab);
  const setActiveTab = useNoteFormatDisplayStore((s) => s.setActiveTab);
  const setActiveFormat = useNoteFormatDisplayStore((s) => s.setActiveFormat);
  const clearActiveFormat = useNoteFormatDisplayStore((s) => s.clearActiveFormat);

  const getDisplayName = (promptValue: string) =>
    displayNameMap[promptValue] || promptValue.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // Safety guard: if activeTab references a missing format, reset to 'original'
  useEffect(() => {
    if (activeTab !== 'original' && !generatedFormats.some((f) => f.id === activeTab)) {
      setActiveTab('original');
      clearActiveFormat();
    }
  }, [activeTab, generatedFormats, setActiveTab, clearActiveFormat]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    if (newValue === 'original') {
      clearActiveFormat();
    } else {
      const format = generatedFormats.find((f) => f.id === newValue);
      if (format) {
        setActiveFormat(format, getDisplayName(format.prompt_value));
      }
    }
  };

  // No formats → render children directly, no tab bar
  if (generatedFormats.length === 0) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mt: 2, mb: 1, maxWidth: '800px', width: '100%' }}>
        <StyledTabs value={activeTab} onChange={handleTabChange}>
          <StyledTab label="Original" value="original" />
          {generatedFormats.map((format) => (
            <StyledTab key={format.id} label={getDisplayName(format.prompt_value)} value={format.id} />
          ))}
        </StyledTabs>
      </Box>

      {activeTab === 'original' ? <>{children}</> : <InlineFormatResult hideCloseButton readOnly={readOnly} />}
    </Box>
  );
};

export default NoteContentTabs;
